import {
   BillingParty,
   Reference,
   Tradeshow,
   COD,
   POD,
   Risks,
} from '@library/common/models';

export interface InvoiceCalculator {
   item: number;
   itemType: string;

   pieces: number;
   piecesType: string;

   length: number;
   width: number;
   height: number;
   dimsType: string;

   weight: number;
   weightType: string;

   cubicFt: number;
   cubicM: number;

   marks: string;
}

export class InvoiceCalculator implements InvoiceCalculator {
   constructor( i?: InvoiceCalculator ) {
      this.item = i?.item;
      this.itemType = i?.itemType;

      this.pieces = i?.pieces;
      this.piecesType = i?.piecesType;

      this.length = i?.length;
      this.width = i?.width;
      this.height = i?.height;
      this.dimsType = i?.dimsType;

      this.weight = i?.weight;
      this.weightType = i?.weightType;

      this.cubicFt = i?.cubicFt;
      this.cubicM = i?.cubicM;

      this.marks = i?.marks;
   }
}

export interface Charge {
   description: string;
   quantity: number;
   rate: number;
   amount: number;
   currency: string;
}

export class Charge implements Charge {
   constructor( c?: Charge ) {
      this.description = c?.description;
      this.quantity = c?.quantity;
      this.rate = c?.rate;
      this.amount = c?.amount;
      this.currency = c?.currency;
   }
}

export enum InvoiceTypes {
   single = 'Single Invoice',
   additional = 'Additional Invoice',
   credit = 'Credit Memo',
   consolidated = 'Consolidated Invoice'
}

export type InvoiceTypesType = keyof typeof InvoiceTypes;

export interface InvoiceHeader {
   id: number;
   consignee: BillingParty;
   invoicedate: Date;
   type: InvoiceTypesType;
   total: number;
   currency: string;

   toString: () => string;
}

export class InvoiceHeader implements InvoiceHeader {
   constructor( h: any = {} ) {
      const invDate = new Date();

      // Reset time to midnight
      invDate.setHours(0, 0, 0, 0);

      if ( h?.invoicedate ) {
         // Date from backend is in format yyyy-mm-dd
         const [y, m, d] = (h.invoicedate as string).split('-');

         // Set the date part with what the server sent us.
         invDate.setFullYear(+y);
         invDate.setMonth((+m) - 1); // Month starts at zero(0) for January
         invDate.setDate(+d);
      }

      this.id = h?.id;
      this.consignee = new BillingParty( h?.consignee );
      this.invoicedate = invDate;
      this.type = h?.type === 'original' ? 'single' : h?.type;
      this.total = h?.total;
      this.currency = h?.currency;
   }

   public toString = (): string => {
      return [
         this.id.toString(),
         this.consignee.toString(),
         this.invoicedate.toString(),
         this.type.toString(),
         this.total.toString(),
         this.currency
      ].join('◬').toLowerCase();
   }
}

export interface InvoiceHawb {
   hawb: string;
   servicelevel: string;

   total: number;
   currency: string;

   invoicedate: Date;
   pickupdate: Date;

   totalpieces: number;
   actualweightlbs: number;
   actualweightkgs: number;
   volumeweightlbs: number;
   volumeweightkgs: number;
   chargeableweightlbs: number;
   chargeableweightkgs: number;

   bill_to: BillingParty;
   shipper: BillingParty;
   consignee: BillingParty;

   origin?: string;
   destination?: string;

   calculator?: InvoiceCalculator[];
   charges?: Charge[];

   references?: Reference[];
   tradeshow?: Tradeshow[];

   cod?: COD;
   pod?: POD;
   risks?: Risks;

   description?: string;
   notes?: string;

   shipmentAccess: boolean;
}

export class InvoiceHawb implements InvoiceHawb {
   constructor( ih = {} as any ) {
      this.hawb = ih?.hawb;
      this.servicelevel = ih?.servicelevel;

      this.total = ih?.total;
      this.currency = ih?.currency;

      this.invoicedate = new Date( ih?.invoicedate );
      this.pickupdate = new Date( ih?.pickupdate );

      this.totalpieces = ih?.totalpieces;
      this.actualweightlbs = ih?.actualweightlbs;
      this.actualweightkgs = ih?.actualweightkgs;
      this.volumeweightlbs = ih?.volumeweightlbs;
      this.volumeweightkgs = ih?.volumeweightkgs;
      this.chargeableweightlbs = ih?.chargeableweightlbs;
      this.chargeableweightkgs = ih?.chargeableweightkgs;

      this.bill_to = new BillingParty( ih?.bill_to );
      this.shipper = new BillingParty( ih?.shipper );
      this.consignee = new BillingParty( ih?.consignee );

      if ( ih?.origin ) { this.origin = ih?.origin; }
      if ( ih?.destination ) { this.destination = ih?.destination; }

      if ( ih?.calculator ) { this.calculator = ih?.calculator?.map( i => new InvoiceCalculator( i )); }
      if ( ih?.charges ) { this.charges = ih?.charges?.map( c => new Charge( c )); }

      if ( ih?.references ) { this.references = ih?.references?.map( r => new Reference( r )); }
      if ( ih?.tradeshow ) { this.tradeshow = ih?.tradeshow?.map( t => new Tradeshow( t )); }

      if ( ih?.cod ) { this.cod = new COD( ih?.cod ); }
      if ( ih?.pod ) { this.pod = new POD( ih?.pod ); }
      if ( ih?.risks ) { this.risks = new Risks( ih?.risks ); }

      if ( ih?.description ) { this.description = ih?.description; }
      if ( ih?.notes ) { this.notes = ih?.notes; }

      this.shipmentAccess = !!ih?.shipmentAccess;
   }
}

export interface Invoice {
   id: number;

   type: InvoiceTypesType;
   // tmTypes: 'FWS' | 'FWSD' | 'FWCM';

   account: number;
   terms: string;

   invoicedate: Date;
   duedate: Date;

   total: number;
   currency: string;

   station: BillingParty;

   notes?: string;

   attached: InvoiceHawb[];
}

export class Invoice implements Invoice {
   constructor( i: any = {} ) {
      this.id = i?.id;
      this.type = i?.type === 'original' ? 'single' : i?.type;
      this.account = i?.account;
      this.terms = i?.terms;
      this.invoicedate = new Date( i?.invoicedate );
      this.duedate = new Date( i?.duedate );
      this.total = i?.total;
      this.currency = i?.currency;
      this.station = new BillingParty( i?.station );
      this.notes = i?.notes || '';
      this.attached = i?.attached.map( hawb => new InvoiceHawb( hawb )) || [];
   }
}
